/**
 * PopupEngine Lite
 * * Features:
 * - Triggers: Exit, Time, Scroll, Pages
 * - Success Logic: AJAX first -> Native Fallback -> Auto-Reopen on Success
 * - "No-Bloat": No countdowns, no clipboards.
 * - URL Cleaning: Hides "?customer_posted=true" after reload.
 */
class PopupEngine extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.triggers = {
      exitIntent: false,
      timeDelay: false,
      scrollPercentage: false,
      pageCount: false,
    };

    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  connectedCallback() {
    this.config = {
      sectionId: this.dataset.sectionId,
      enabled: this.dataset.enabled === 'true',
      forcePreview: this.dataset.forcePreview === 'true',
      timingMode: this.dataset.timing,
      exitIntentEnabled: this.dataset.exitIntent === 'true',
      timeDelay: parseInt(this.dataset.timeDelay, 10) * 1000,
      scrollPercentage: parseInt(this.dataset.scrollPercentage, 10),
      pageCount: parseInt(this.dataset.pageCount, 10),
      allConditions: this.dataset.allConditions === 'true',
      frequencyDays: parseInt(this.dataset.frequency, 10),
      gdprEnabled: this.dataset.gdprEnabled === 'true',
    };

    // 1. Theme Editor Logic
    if (this.isThemeEditor()) {
      if (this.config.enabled) {
        if (this.config.forcePreview) {
          this.show(); // Show success/main based on liquid setting
        } else {
          // In editor, standard mode, inject main template but don't show to keep editor clean
          const template = this.querySelector('template[data-template="main"]');
          if (template) this.appendChild(template.content.cloneNode(true));
        }
      }
      return;
    }

    // 2. Live Site Logic
    if (!this.config.enabled) return;

    // A. Inject HTML (Main View)
    const template = this.querySelector('template[data-template="main"]');
    if (template) {
      this.appendChild(template.content.cloneNode(true));
    }

    // B. Check for "Post-Redirect Success" (The Captcha Fix)
    // If the user was forced to reload to solve a captcha, this logic
    // ensures the popup re-opens immediately to show the discount code.
    const isPostRedirect = window.location.search.includes('customer_posted=true');
    const wasSubmitting = sessionStorage.getItem(`popup-submitting-${this.config.sectionId}`);

    if (isPostRedirect && wasSubmitting === 'true') {
      // Clear flag so it doesn't open on next refresh
      sessionStorage.removeItem(`popup-submitting-${this.config.sectionId}`);
      // Mark as shown/converted in local storage
      localStorage.setItem(`popup-shown-${this.config.sectionId}`, Date.now().toString());
      
      // Clean URL (Remove ?customer_posted=true and #hash)
      if (window.history.replaceState) {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({path: cleanUrl}, '', cleanUrl);
      }

      // Show Success View immediately
      this.show();
      this.showSuccessState();
      return; 
    }

    // C. Standard Trigger Logic
    if (this.shouldShowBasedOnFrequency()) {
      if (this.config.timingMode === 'immediate') {
        this.show();
      } else {
        this.setupTriggers();
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  isThemeEditor() {
    return window.Shopify && window.Shopify.designMode;
  }

  shouldShowBasedOnFrequency() {
    if (this.config.frequencyDays === 0) return true;
    
    const storageKey = `popup-shown-${this.config.sectionId}`;
    const lastShown = localStorage.getItem(storageKey);
    if (!lastShown) return true;

    const daysSinceShown = (Date.now() - parseInt(lastShown, 10)) / (1000 * 60 * 60 * 24);
    return daysSinceShown >= this.config.frequencyDays;
  }

  setupEventListeners() {
    const closeButton = this.querySelector('.popup-engine-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.handleFormSubmit);
    }

    // Close on overlay click
    this.addEventListener('click', (e) => {
      if (e.target === this) this.hide();
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) this.hide();
    });
  }

  /**
   * ROBUST FORM HANDLING
   * 1. Try AJAX.
   * 2. If Shopify asks for Captcha (challenge), force native submit.
   * 3. Set a session flag so if native submit reloads page, we know to re-open popup.
   */
  async handleFormSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const submitButton = form.querySelector('button[type="submit"]');
    
    // Disable button
    submitButton.disabled = true;
    submitButton.textContent = '...';

    // Set flag in case we are forced to reload
    sessionStorage.setItem(`popup-submitting-${this.config.sectionId}`, 'true');

    try {
      // Construct Shopify contact URL
      const routes = window.Shopify && window.Shopify.routes ? window.Shopify.routes : { root: '/' };
      const contactUrl = routes.root + 'contact';

      const formData = new FormData(form);
      
      const response = await fetch(contactUrl, {
        method: 'POST',
        headers: { 
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript'
        }, 
        body: formData
      });

      // Analyze response
      const text = await response.text();

      // CASE 1: Captcha / Challenge required
      // Shopify returns HTML containing 'g-recaptcha' or redirects to /challenge
      if (response.url.includes('/challenge') || text.includes('g-recaptcha') || text.includes('h-captcha')) {
        // Fallback to native submit -> Page will reload -> connectedCallback handles the rest
        form.submit(); 
        return;
      }

      // CASE 2: Success (200 OK)
      // Usually signifies the email was accepted without challenge
      if (response.ok) {
        sessionStorage.removeItem(`popup-submitting-${this.config.sectionId}`); // Clear flag, we didn't reload
        this.showSuccessState();
      } else {
        throw new Error('Network response was not ok');
      }

    } catch (error) {
      console.log('Popup: Background submit failed, falling back to native.', error);
      // Fallback: Just submit the form natively.
      // This ensures we never lose the customer data.
      form.submit();
    }
  }

  showSuccessState() {
    const mainContainer = this.querySelector('.popup-engine-container');
    const successTemplate = this.querySelector('template[data-template="success"]');

    if (mainContainer && successTemplate) {
      const newContent = successTemplate.content.cloneNode(true);
      // Replace the entire container to ensure clean state
      mainContainer.replaceWith(newContent);
      
      // Re-bind close button
      const newClose = this.querySelector('.popup-engine-close');
      if (newClose) newClose.addEventListener('click', () => this.hide());
    }

    // Mark as converted so it doesn't show again
    localStorage.setItem(`popup-shown-${this.config.sectionId}`, Date.now().toString());
  }

  /**
   * TRIGGERS
   */
  setupTriggers() {
    // 1. Exit Intent
    if (this.config.exitIntentEnabled) {
      document.addEventListener('mouseleave', this.handleMouseLeave);
    }
    
    // 2. Time Delay
    if (this.config.timeDelay > 0) {
      setTimeout(() => {
        this.triggers.timeDelay = true;
        this.checkTriggers();
      }, this.config.timeDelay);
    } else {
        this.triggers.timeDelay = true;
    }

    // 3. Scroll
    if (this.config.scrollPercentage > 0) {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    } else {
        this.triggers.scrollPercentage = true;
    }

    // 4. Page Count
    if (this.config.pageCount > 0) {
      this.setupPageCountTrigger();
    } else {
        this.triggers.pageCount = true;
    }
    
    this.checkTriggers();
  }

  handleMouseLeave(e) {
    if (e.clientY <= 0) {
      this.triggers.exitIntent = true;
      this.checkTriggers();
      document.removeEventListener('mouseleave', this.handleMouseLeave);
    }
  }

  handleScroll() {
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;
    const scrolled = (window.scrollY / scrollableHeight) * 100;

    if (scrolled >= this.config.scrollPercentage) {
      this.triggers.scrollPercentage = true;
      this.checkTriggers();
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  setupPageCountTrigger() {
    const storageKey = `popup-page-views-count`; // Global counter
    let pageViews = parseInt(sessionStorage.getItem(storageKey) || '0', 10);
    // Increment only once per session/page load logic, but simpler to just read
    // actually, we should increment it.
    if (!window.popupPageCounted) {
        pageViews++;
        sessionStorage.setItem(storageKey, pageViews.toString());
        window.popupPageCounted = true;
    }

    if (pageViews >= this.config.pageCount) {
      this.triggers.pageCount = true;
      this.checkTriggers();
    }
  }

  checkTriggers() {
    if (this.isVisible) return;

    const enabledTriggers = [];
    if (this.config.exitIntentEnabled) enabledTriggers.push('exitIntent');
    if (this.config.timeDelay > 0) enabledTriggers.push('timeDelay');
    if (this.config.scrollPercentage > 0) enabledTriggers.push('scrollPercentage');
    if (this.config.pageCount > 0) enabledTriggers.push('pageCount');
    
    if(enabledTriggers.length === 0 && this.config.timingMode === 'rules') return;

    const triggeredConditions = enabledTriggers.filter(trigger => this.triggers[trigger]);

    const shouldShow = this.config.allConditions
      ? triggeredConditions.length === enabledTriggers.length
      : triggeredConditions.length > 0;

    if (shouldShow) {
      this.show();
    }
  }

  show() {
    if (this.isVisible) return;

    // GDPR Check
    if (this.config.gdprEnabled && window.Shopify?.customerPrivacy) {
        const consent = window.Shopify.customerPrivacy.getTrackingConsent();
        if (consent === 'no') return; 
    }

    this.classList.add('active');
    this.isVisible = true;
    document.body.style.overflow = 'hidden';

    this.setupEventListeners();
    
    // Only set "shown" timestamp if not in success state (to avoid frequency capping loop on success)
    if (!this.querySelector('.popup-engine-success-box--1')) {
         // Standard view open
    }
  }

  hide() {
    this.classList.remove('active');
    this.isVisible = false;
    document.body.style.overflow = '';
    
    // If we hide it, we assume it's "seen".
    if (!this.isThemeEditor()) {
        localStorage.setItem(`popup-shown-${this.config.sectionId}`, Date.now().toString());
    }
  }
}

if (!customElements.get('popup-engine')) {
  customElements.define('popup-engine', PopupEngine);
}